import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canManage } from '@/lib/roles'
import { engagementsApi, type EngagementSummary } from '@/lib/engagementsApi'
import { EngagementContextMenu, type EngagementContextMenuTarget } from '@/components/pentest/EngagementContextMenu'

type SortKey = 'submittedDesc' | 'nameAsc' | 'clientAsc'

const fmtRelative = (s: string): string => {
  try {
    const ms = Date.now() - new Date(s).getTime()
    if (!isFinite(ms) || ms < 0) return new Date(s).toLocaleString()
    const sec = Math.floor(ms / 1000)
    if (sec < 60)        return `${sec}s ago`
    const min = Math.floor(sec / 60)
    if (min < 60)        return `${min}m ago`
    const hr = Math.floor(min / 60)
    if (hr < 48)         return `${hr}h ago`
    const day = Math.floor(hr / 24)
    if (day < 30)        return `${day}d ago`
    return new Date(s).toLocaleDateString()
  } catch { return s }
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const tone = status === 'submitted'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${tone}`}>
      {status}
    </span>
  )
}

export const PentestHistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { error: showError, success: showSuccess } = useToast()
  const { user } = useAuth()
  const canManageEngagements = canManage(user)

  const [engagements, setEngagements] = useState<EngagementSummary[]>([])
  const [loading,     setLoading]     = useState(true)
  const [filterText,  setFilterText]  = useState('')
  const [sortBy,      setSortBy]      = useState<SortKey>('submittedDesc')
  const [menuTarget,  setMenuTarget]  = useState<EngagementContextMenuTarget | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      // Non-admin without a hwid would 403; show empty state instead.
      if (!canManageEngagements) {
        setEngagements([])
        return
      }
      setEngagements(await engagementsApi.list())
    } catch (err: any) {
      showError(t('pentest.history.loadFailed', 'Failed to load engagements') as string, err?.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [canManageEngagements])

  const visible = useMemo(() => {
    let rows = engagements
    const q = filterText.trim().toLowerCase()
    if (q) {
      rows = rows.filter(e =>
        e.name.toLowerCase().includes(q) ||
        (e.client_name ?? '').toLowerCase().includes(q) ||
        (e.surveyor_email ?? '').toLowerCase().includes(q)
      )
    }
    const sorted = [...rows]
    switch (sortBy) {
      case 'nameAsc':
        sorted.sort((a, b) => a.name.localeCompare(b.name)); break
      case 'clientAsc':
        sorted.sort((a, b) => (a.client_name ?? '').localeCompare(b.client_name ?? '')); break
      case 'submittedDesc':
      default:
        sorted.sort((a, b) => b.submitted_at.localeCompare(a.submitted_at))
    }
    return sorted
  }, [engagements, filterText, sortBy])

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="mx-auto max-w-[1400px] px-4 py-4">

        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
            title={t('common.backToDashboard', 'Back to Dashboard') as string}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-slate-400">
              <span className="hover:underline cursor-pointer text-slate-500" onClick={() => navigate('/dashboard')}>
                {t('nav.dashboard', 'Dashboard')}
              </span>
              <span> &rsaquo; </span>
              <span>{t('pentest.history.title', 'Pentest History')}</span>
            </div>
            <h1 className="text-lg font-semibold mt-0.5 leading-tight">
              {t('pentest.history.title', 'Pentest History')}
              <span className="ml-2 text-xs font-normal text-slate-400">
                {visible.length}
                {filterText && engagements.length !== visible.length && (
                  <> / {engagements.length}</>
                )}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={load}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              title={t('common.refresh', 'Refresh') as string}
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative w-80 max-w-full">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder={t('pentest.history.filterPlaceholder', 'Filter by name, client, surveyor…') as string}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as SortKey)}
            className="text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 px-2 py-1.5"
          >
            <option value="submittedDesc">
              {t('pentest.history.sortBy.submittedDesc', 'Newest first')}
            </option>
            <option value="nameAsc">
              {t('pentest.history.sortBy.nameAsc', 'Name (A→Z)')}
            </option>
            <option value="clientAsc">
              {t('pentest.history.sortBy.clientAsc', 'Client (A→Z)')}
            </option>
          </select>
        </div>

        {/* Empty / loading / non-admin states */}
        {!canManageEngagements && !loading && (
          <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-8 text-center">
            <p className="text-sm text-slate-500">
              {t('pentest.history.adminOnly',
                'Pentest history requires admin access. Contact your administrator.')}
            </p>
          </div>
        )}

        {canManageEngagements && !loading && visible.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-8 text-center">
            <p className="text-sm text-slate-500">
              {filterText
                ? t('pentest.history.noMatch', 'No engagements match the filter.')
                : t('pentest.history.empty',
                    'No pentest history yet. Run an assessment in SEACURE(TOOL) and submit it to see results here.')}
            </p>
          </div>
        )}

        {canManageEngagements && loading && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-3">
            <div className="space-y-1.5">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="h-6 rounded bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        {canManageEngagements && !loading && visible.length > 0 && (
          <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/40">
                  <tr className="text-left text-slate-500 dark:text-slate-400">
                    <th className="px-3 py-2 font-medium">{t('pentest.history.column.submitted', 'Submitted')}</th>
                    <th className="px-3 py-2 font-medium">{t('pentest.history.column.name',      'Name')}</th>
                    <th className="px-3 py-2 font-medium">{t('pentest.history.column.client',    'Client')}</th>
                    <th className="px-3 py-2 font-medium">{t('pentest.history.column.surveyor',  'Surveyor')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('pentest.history.column.hosts',   'Hosts')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('pentest.history.column.attacks', 'Attacks')}</th>
                    <th className="px-3 py-2 font-medium">{t('pentest.history.column.status',    'Status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {visible.map(e => (
                    <tr
                      key={e.id}
                      onClick={() => navigate(`/pentest-history/${e.id}`)}
                      onContextMenu={ev => {
                        ev.preventDefault()
                        setMenuTarget({ engagement: e, x: ev.clientX, y: ev.clientY })
                      }}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40"
                    >
                      <td className="px-3 py-2 whitespace-nowrap" title={new Date(e.submitted_at).toLocaleString()}>
                        {fmtRelative(e.submitted_at)}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-200">
                        {e.name}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                        {e.client_name ?? <span className="text-slate-300 dark:text-slate-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {e.surveyor_email ?? '—'}{' '}
                        <span
                          className="font-mono text-[10px] text-slate-400"
                          title={e.surveyor_hardware_id}
                        >
                          ({e.surveyor_hardware_id.slice(0, 8)}…)
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-300">
                        {e.assets_count}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600 dark:text-slate-300">
                        {e.attack_runs_count}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={e.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tip */}
        {canManageEngagements && !loading && visible.length > 0 && (
          <p className="mt-2 text-[10px] text-slate-400">
            {t('pentest.history.tipWithMenu',
               'Click a row to open it. Right-click to edit details or delete.')}
          </p>
        )}

      </div>

      <EngagementContextMenu
        target={menuTarget}
        onClose={() => setMenuTarget(null)}
        onUpdated={() => {
          showSuccess(t('pentest.history.updated', 'Engagement updated') as string)
          load()
        }}
        onDeleted={() => {
          showSuccess(t('pentest.history.deleted', 'Engagement deleted') as string)
          load()
        }}
        onError={msg => showError(t('pentest.history.actionFailed', 'Action failed') as string, msg)}
      />
    </div>
  )
}
